import { useApolloClient } from '@apollo/client/react';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { TableCell } from 'common/components/Table';
import { Checkbox } from 'common/components/Checkbox';
import { IFeature, IUserManagementListItemFragment } from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { Chip, ChipGroup } from 'common/components/Chip';
import { formatDate } from 'common/utils/formatDate';
import { UserAvatar } from 'user/components/UserAvatar';
import { LastActiveLabel } from 'user/components/LastActiveLabel';

interface IProps {
    disabled?: boolean;
    isEditable?: boolean;
    isSelected?: boolean;
    user: IUserManagementListItemFragment;
    onSelect?(): void;
    onArchive?(): void;
    onDelete?(): void;
    onUpdate?(): void;
    forArchive?: boolean;
}

export const UserManagementTableRow = ({
    disabled,
    isEditable = true,
    isSelected,
    user,
    onSelect,
    onArchive,
    onDelete,
    onUpdate,
    forArchive,
}: IProps) => {
    const [translate] = useTranslation();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();
    const client = useApolloClient();
    const { canUse: hasUserActivity } = useFeature(IFeature.UserActivity);

    const {
        id,
        name,
        roles,
        invitedOn,
        lastLogin,
        lastActivity,
        userVia,
        archivedAt,
        participantGroups,
    } = user;

    const lastActiveDate = hasUserActivity ? lastActivity?.endedAt : lastLogin;

    const evictUsers = () => {
        client.cache.evict({
            fieldName: 'paginatedUsers',
        });

        client.cache.gc();
    };

    const actionDate = forArchive ? archivedAt : invitedOn;

    const groupAmount = 5;
    const groupsDisplay = participantGroups?.length ? (
        <>
            {participantGroups
                .slice(0, groupAmount)
                .map((group) => group.name)
                .join(', ')}
            {participantGroups.length > groupAmount &&
                `, ${translate('andMoreResults', {
                    count: participantGroups.length - groupAmount,
                })}`}
        </>
    ) : (
        '-'
    );

    return (
        <TableRow>
            {isEditable && !!onSelect && (
                <TableCell>
                    <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        onClick={(e) => {
                            onSelect?.();

                            e.stopPropagation();
                        }}
                    />
                </TableCell>
            )}

            <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                    openGlobalDrawer({
                        type: IGlobalDrawerType.UserProfile,
                        itemId: id,
                        props: {
                            onUpdate: () => {
                                evictUsers();
                                onUpdate?.();
                            },
                            onArchive: () => {
                                evictUsers();

                                onArchive?.();

                                closeGlobalDrawer({
                                    type: IGlobalDrawerType.UserProfile,
                                });
                            },
                            onDelete: () => {
                                evictUsers();

                                onDelete?.();

                                closeGlobalDrawer({
                                    type: IGlobalDrawerType.UserProfile,
                                });
                            },
                        },
                    });
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <UserAvatar bgColor="secondary" size={45} user={user} />

                    <Box sx={{ ml: 2 }}>
                        <Typography
                            color={disabled ? 'grey.500' : undefined}
                            variant="body2"
                        >
                            {name}
                        </Typography>

                        <ChipGroup sx={{ my: 0.25 }}>
                            {roles?.filter(Boolean).map((role) => (
                                <Chip
                                    key={role}
                                    label={translate(
                                        `roles.${role.toLowerCase()}`
                                    )}
                                    size="small"
                                />
                            ))}
                        </ChipGroup>
                    </Box>
                </Box>
            </TableCell>
            <TableCell>{groupsDisplay}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {actionDate ? formatDate(actionDate, 'd LLL yyyy HH:mm') : '-'}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <LastActiveLabel
                    showTooltip
                    activity={{ endedAt: lastActiveDate }}
                />
            </TableCell>
            <TableCell>
                {!!userVia?.length ? userVia.join(', ') : '-'}
            </TableCell>
        </TableRow>
    );
};
